import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileText, CheckCircle2, FilePen, Mail, Clock3, Pencil, Trash2, Eye, EyeOff, Plus, Send, Check, Undo2, X, Archive } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { CATEGORY_META, formatDate } from "@/lib/constants";
import { ARTICLE_STATES } from "@/lib/media";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}18` }}><Icon size={22} style={{ color }} /></div>
      <div><p className="text-2xl font-black text-nx-dark">{value}</p><p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p></div>
    </div>
  );
}

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "in_review", label: "En revisión" },
  { key: "draft", label: "Borradores" },
  { key: "published", label: "Publicados" },
  { key: "approved", label: "Aprobados" },
  { key: "rejected", label: "Rechazados" },
  { key: "archived", label: "Archivados" },
];

function IconBtn({ onClick, title, testid, children, danger }) {
  return <button onClick={onClick} title={title} data-testid={testid}
    className={`p-2 rounded hover:bg-gray-100 ${danger ? "text-red-500 hover:bg-red-50" : "text-gray-500"}`}>{children}</button>;
}

export default function AdminDashboard() {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([api.get("/admin/articles"), api.get("/admin/stats")]);
      setArticles(a.data.items); setStats(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action, body) => {
    try { await api.post(`/admin/articles/${id}/${action}`, body || {}); toast.success("Acción realizada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Acción no permitida"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/articles/${id}`); toast.success("Artículo eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };
  const returnWithNote = async (id) => {
    const note = window.prompt("Observaciones para el autor (opcional):", "");
    if (note === null) return;
    act(id, "return", { note });
  };

  const filtered = filter === "all" ? articles : articles.filter((a) => a.status === filter);

  return (
    <AdminLayout title="Panel de control">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat icon={FileText} label="Total" value={stats.total ?? "–"} color="#0055FF" />
        <Stat icon={Clock3} label="En revisión" value={stats.in_review ?? "–"} color="#8A2BE2" />
        <Stat icon={CheckCircle2} label="Publicados" value={stats.published ?? "–"} color="#16a34a" />
        <Stat icon={FilePen} label="Borradores" value={stats.drafts ?? "–"} color="#FF6B35" />
        <Stat icon={Mail} label="Mensajes" value={stats.contacts ?? "–"} color="#8A2BE2" />
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${filter === f.key ? "bg-nx-dark text-white border-nx-dark" : "border-gray-200 text-gray-500"}`}>{f.label}</button>
          ))}
        </div>
        <Link to="/redaccion-nexodum/nuevo" data-testid="admin-new-article-btn" className="inline-flex items-center gap-2 nx-gradient-bg text-white font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wider"><Plus size={15} /> Nuevo</Link>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-articles-table">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-4 py-3">Título</th><th className="px-4 py-3">Sección</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Autor / Editor</th><th className="px-4 py-3 text-right">Acciones</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Cargando…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Sin artículos en este estado.</td></tr>
                : filtered.map((a) => {
                  const st = ARTICLE_STATES[a.status] || ARTICLE_STATES.draft;
                  return (
                    <tr key={a.id} data-testid={`admin-row-${a.id}`} className="hover:bg-gray-50 align-top">
                      <td className="px-4 py-3 max-w-xs"><p className="font-medium text-nx-dark line-clamp-2">{a.title}</p><p className="text-[11px] text-gray-400 mt-0.5">{formatDate(a.updated_at || a.date)}</p></td>
                      <td className="px-4 py-3"><span className="text-xs font-bold uppercase" style={{ color: CATEGORY_META[a.category]?.color }}>{CATEGORY_META[a.category]?.label || a.category}</span></td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-bold uppercase px-2 py-1 rounded ${st.bg}`}>{st.label}</span>{a.review_note && <p className="text-[10px] text-gray-400 mt-1 max-w-[140px]">Obs: {a.review_note}</p>}</td>
                      <td className="px-4 py-3 text-xs text-gray-500"><p>{a.author || "—"}</p>{a.editor_responsable && <p className="text-gray-400">Ed: {a.editor_responsable}</p>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-0.5 flex-wrap">
                          {a.status === "in_review" && can("approve") && <IconBtn onClick={() => act(a.id, "approve")} title="Aprobar" testid={`approve-${a.id}`}><Check size={16} className="text-green-600" /></IconBtn>}
                          {a.status === "in_review" && can("review") && <IconBtn onClick={() => returnWithNote(a.id)} title="Devolver con observaciones" testid={`return-${a.id}`}><Undo2 size={16} /></IconBtn>}
                          {(a.status === "in_review" || a.status === "draft") && can("review") && <IconBtn onClick={() => act(a.id, "reject", { note: "" })} title="Rechazar" testid={`reject-${a.id}`}><X size={16} className="text-red-500" /></IconBtn>}
                          {can("submit") && ["draft", "rejected"].includes(a.status) && <IconBtn onClick={() => act(a.id, "submit")} title="Enviar a revisión" testid={`submit-${a.id}`}><Send size={15} className="text-nx-violet" /></IconBtn>}
                          {can("publish") && a.status !== "published" && <IconBtn onClick={() => act(a.id, "publish")} title="Publicar" testid={`publish-${a.id}`}><Eye size={16} className="text-green-600" /></IconBtn>}
                          {can("unpublish") && a.status === "published" && <IconBtn onClick={() => act(a.id, "unpublish")} title="Despublicar" testid={`unpublish-${a.id}`}><EyeOff size={16} /></IconBtn>}
                          {can("archive") && a.status !== "archived" && <IconBtn onClick={() => act(a.id, "archive")} title="Archivar" testid={`archive-${a.id}`}><Archive size={15} /></IconBtn>}
                          <Link to={`/redaccion-nexodum/editar/${a.id}`} data-testid={`edit-${a.id}`} className="p-2 rounded hover:bg-gray-100 text-gray-500" title="Editar"><Pencil size={16} /></Link>
                          {can("delete") && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild><button className="p-2 rounded hover:bg-red-50 text-red-500" data-testid={`delete-${a.id}`} title="Eliminar"><Trash2 size={16} /></button></AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer. Se eliminará “{a.title}”.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(a.id)} data-testid={`confirm-delete-${a.id}`} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
